import { useEffect, useRef, useState } from 'react';
import { X, Terminal, RefreshCw, ExternalLink } from 'lucide-react';
import { adminApi } from '../../services/api';
import { useSocket } from '../../contexts/SocketContext';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import type { AdminHostAgent } from '../../types/admin';

interface SSHModalProps {
  hostAgent: AdminHostAgent;
  onClose: () => void;
}

export function SSHModal({ hostAgent, onClose }: SSHModalProps) {
  const { sshSocket: socket } = useSocket();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sshConnected, setSshConnected] = useState(false);
  const [, setCwd] = useState<string>('~');

  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  const username = hostAgent.sshDetails?.user || 'root';

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Initialize xterm
  useEffect(() => {
    if (!terminalRef.current || xtermRef.current) return;
    const term = new XTerm({
      convertEol: false,
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: 14,
      scrollback: 2000,
      theme: {
        background: '#0a0a0f',
        foreground: '#e5e5e5',
        cursor: '#00e85e',
        cursorAccent: '#0a0a0f',
        selectionBackground: '#00e85e33',
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(terminalRef.current);
    fit.fit();
    xtermRef.current = term;
    fitAddonRef.current = fit;
    let disposed = false;

    const onWindowResize = () => {
      if (disposed) return;
      try { fit.fit(); } catch { return; }
      if (socket && sessionIdRef.current && term.cols && term.rows) {
        socket.emit('ssh-resize', { sessionId: sessionIdRef.current, cols: term.cols, rows: term.rows });
      }
    };
    window.addEventListener('resize', onWindowResize);
    const ro = new ResizeObserver(() => onWindowResize());
    try { if (terminalRef.current) ro.observe(terminalRef.current); } catch {}
    const dispResize = term.onResize(({ cols, rows }) => {
      if (!disposed && socket && sessionIdRef.current) {
        socket.emit('ssh-resize', { sessionId: sessionIdRef.current, cols, rows });
      }
    });

    return () => {
      disposed = true;
      window.removeEventListener('resize', onWindowResize);
      try { ro.disconnect(); } catch {}
      dispResize.dispose();
      term.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, [socket]);

  // Bridge xterm input to backend
  useEffect(() => {
    const term = xtermRef.current;
    if (!term || !socket) return;
    const disp = term.onData((data: string) => {
      if (sessionIdRef.current) socket.emit('ssh-input', { sessionId: sessionIdRef.current, data });
    });
    return () => disp.dispose();
  }, [socket]);

  // Socket events
  useEffect(() => {
    if (!socket) return;

    const onSSHConnected = (data: {
      requestId?: string;
      sessionId: string;
      hostAgent: { name: string; ipAddress: string; username: string };
      cwd?: string;
    }) => {
      if (data.requestId && data.requestId !== requestIdRef.current) return;
      sessionIdRef.current = data.sessionId;
      setSessionId(data.sessionId);
      setSshConnected(true);
      setIsConnecting(false);
      if (data.cwd) setCwd(data.cwd);
      const term = xtermRef.current;
      if (term) {
        term.writeln(`\x1b[32m●\x1b[0m Connected to ${data.hostAgent.name}`);
        setTimeout(() => {
          if (xtermRef.current !== term) return;
          fitAddonRef.current?.fit();
          term.focus();
          socket.emit('ssh-resize', { sessionId: data.sessionId, cols: term.cols, rows: term.rows });
        }, 0);
      }
    };

    const onSSHOutput = (data: { requestId?: string; stdout?: string; stderr?: string; stdoutChunk?: string; stderrChunk?: string; cwd?: string }) => {
      if (data.requestId && data.requestId !== requestIdRef.current) return;
      if (data.cwd) setCwd(data.cwd.replace(/\r?\n$/, ''));
      const term = xtermRef.current;
      if (!term) return;
      if (data.stdout) term.write(data.stdout);
      if (data.stderr) term.write(data.stderr);
      if (data.stdoutChunk) term.write(data.stdoutChunk);
      if (data.stderrChunk) term.write(data.stderrChunk);
    };

    const onSSHError = (data: { requestId?: string; message: string }) => {
      if (data.requestId && data.requestId !== requestIdRef.current) return;
      setIsConnecting(false);
      xtermRef.current?.writeln(`\x1b[31mError:\x1b[0m ${data.message}`);
    };

    const onSSHDisconnected = (data?: { requestId?: string }) => {
      if (data?.requestId && data.requestId !== requestIdRef.current) return;
      sessionIdRef.current = null;
      requestIdRef.current = null;
      setSshConnected(false);
      setSessionId(null);
      xtermRef.current?.writeln('\n\x1b[33m●\x1b[0m Connection closed');
    };

    socket.on('ssh-connected', onSSHConnected);
    socket.on('ssh-output', onSSHOutput);
    socket.on('ssh-error', onSSHError);
    socket.on('ssh-disconnected', onSSHDisconnected);

    return () => {
      socket.off('ssh-connected', onSSHConnected);
      socket.off('ssh-output', onSSHOutput);
      socket.off('ssh-error', onSSHError);
      socket.off('ssh-disconnected', onSSHDisconnected);
    };
  }, [socket]);

  const connectSSH = async () => {
    if (!socket || isConnecting || sshConnected) return;
    try {
      setIsConnecting(true);
      xtermRef.current?.clear();
      xtermRef.current?.writeln('\x1b[33m●\x1b[0m Connecting...');

      const resp = await adminApi.initializeSSH(hostAgent._id);
      if (!mountedRef.current) return;
      if (!resp?.success) {
        setIsConnecting(false);
        xtermRef.current?.writeln('\x1b[31m●\x1b[0m Connection failed');
        return;
      }

      const term = xtermRef.current;
      const requestId = globalThis.crypto.randomUUID();
      requestIdRef.current = requestId;
      socket.emit('ssh-connect', { requestId, hostAgentId: hostAgent._id, cols: term?.cols, rows: term?.rows });
    } catch (e) {
      setIsConnecting(false);
      xtermRef.current?.writeln(`\x1b[31mError:\x1b[0m ${(e as Error).message}`);
    }
  };

  const disconnectSSH = () => {
    if (!socket || !sessionId) return;
    socket.emit('ssh-disconnect', { sessionId });
  };

  const openInNewWindow = () => {
    if (sessionId && socket) socket.emit('ssh-disconnect', { sessionId });
    else if (requestIdRef.current && socket) socket.emit('ssh-cancel', { requestId: requestIdRef.current });
    const width = Math.max(900, Math.floor(window.screen.availWidth * 0.7));
    const height = Math.max(600, Math.floor(window.screen.availHeight * 0.8));
    const left = Math.floor((window.screen.availWidth - width) / 2);
    const top = Math.floor((window.screen.availHeight - height) / 2);
    window.open(`/ssh/${hostAgent._id}`, '_blank', `popup=yes,width=${width},height=${height},left=${left},top=${top}`);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (sessionIdRef.current && socket) {
        socket.emit('ssh-disconnect', { sessionId: sessionIdRef.current });
        sessionIdRef.current = null;
      } else if (requestIdRef.current && socket) {
        socket.emit('ssh-cancel', { requestId: requestIdRef.current });
      }
      requestIdRef.current = null;
    };
  }, [socket]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="bg-dark-800 rounded-lg w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden border border-dark-600" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-dark-600 bg-dark-850">
          <div className="flex items-center gap-3">
            <Terminal className="w-5 h-5 text-neon-400" />
            <div>
              <h2 className="font-semibold text-gray-100">{hostAgent.name}</h2>
              <p className="text-xs text-gray-500">{hostAgent.ipAddress}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!sshConnected ? (
              <button onClick={connectSSH} disabled={isConnecting} className="btn-primary text-sm py-1.5 px-3">
                {isConnecting ? <><RefreshCw className="w-4 h-4 animate-spin" /> Connecting</> : <><Terminal className="w-4 h-4" /> Connect</>}
              </button>
            ) : (
              <button onClick={disconnectSSH} className="btn-secondary text-sm py-1.5 px-3">
                Disconnect
              </button>
            )}
            <button onClick={openInNewWindow} className="btn-ghost p-2" title="Open in new window">
              <ExternalLink className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="btn-ghost p-2">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Terminal */}
        <div className="flex-1 bg-[#0a0a0f] overflow-hidden relative">
          {!sshConnected && !isConnecting && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Terminal className="w-12 h-12 text-dark-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">Click Connect to start</p>
                <p className="text-xs text-gray-600 code">ssh {username}@{hostAgent.ipAddress}</p>
              </div>
            </div>
          )}

          {isConnecting && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="spinner w-8 h-8" />
            </div>
          )}

          <div ref={terminalRef} className="h-full w-full" style={{ display: sshConnected ? 'block' : 'none' }} />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-dark-850 border-t border-dark-600 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            <span className="mr-4">Enter = execute</span>
            <span className="mr-4">↑↓ = history</span>
            <span>Ctrl+C = stop</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${sshConnected ? 'bg-neon-400' : 'bg-gray-600'}`} />
            <span className="text-gray-400">{sshConnected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
